import { animate, style, transition, trigger } from '@angular/animations'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { OverlayModule } from '@angular/cdk/overlay'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { AngularReactModule } from '@bubblydoo/angular-react'
import { Store } from '@ngrx/store'
import { OAuthService } from 'angular-oauth2-oidc'
import { TooltipModule } from 'primeng/tooltip'

import { filter, map } from 'rxjs'

import { FlxButtonComponent, FlxCardComponent } from '@flowx/angular-ui-toolkit'
import { urlUpdate$ } from '@flowx/core-sdk'
import { getTenantId } from '@flowx/designer/modules/auth/tenant-id.state'
import { PanelSize } from '@flowx/designer/modules/process-details/process-details-helper.service'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { FlxReusableTemplateInfoComponent } from '@flowx/designer/modules/reusable-ui-template/components/reusable-template-info/reusable-template-info.component'
import { ApplicationType } from '@flowx/designer/shared/entities/application.entity'
import { UiFlowSession } from '@flowx/designer/shared/entities/ui-flow-sessions.types'
import { UiFlowSessionsService } from '@flowx/designer/shared/services/ui-flow-sessions.service'
import {
  selectQueryParam,
  selectRouteParam,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { FlxUiFlowRendererPortal } from '@flowx/react-sdk'

import { UiFlowSessionDetailsPanelComponent } from '../../components/ui-flow-session-details-panel/ui-flow-session-details-panel.component'

@Component({
  selector: 'flx-ui-flow-running-instance',
  imports: [
    RouterLink,
    FlxButtonComponent,
    FlxCardComponent,
    FlxReusableTemplateInfoComponent,
    TooltipModule,
    OverlayModule,
    UiFlowSessionDetailsPanelComponent,
    AngularReactModule,
  ],
  animations: [
    trigger('fadeIn', [
      transition('void => *', [
        style({ transform: 'translateY(100vh)' }),
        animate(300, style({ transform: 'translateY(0)' })),
      ]),
      transition('* => void', [
        style({ transform: 'translateY(0)' }),
        animate(300, style({ transform: 'translateY(100vh)' })),
      ]),
    ]),
  ],
  templateUrl: './ui-flow-running-instance.component.html',
  styleUrl: './ui-flow-running-instance.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: `block pt-20 pr-20 pl-24 h-full overflow-auto flex flex-col h-full flx-ui-flow-renderer`,
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiFlowRunningInstanceComponent implements OnInit, OnDestroy {
  #store = inject(Store)
  #uiFlowSessionsService = inject(UiFlowSessionsService)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #route = inject(ActivatedRoute)
  #destroyRef = inject(DestroyRef)
  #authService = inject(OAuthService)

  #mutationObserver: MutationObserver | null = null
  #baseReactProps: Record<string, unknown>
  #processUuid = toSignal(this.#route.queryParams.pipe(map((params) => params.processUuid))) // selectQueryParam does not emit correctly, so we use toSignal
  #instanceDataOpenedQueryParam = toSignal(
    this.#route.queryParams.pipe(map((params) => params.instanceDataOpened)) // selectQueryParam does not emit correctly, so we use toSignal
  )

  router = inject(Router)
  apiUrl = inject('BASE_URL' as any)
  staticAssetsPath = inject('STATIC_ASSETS_PATH' as any)
  processApiPath = inject('PROCESS_API_PATH' as any)

  uiFlowResourceId = input<string>()

  authToken = signal(localStorage.getItem('access_token'))
  startParams = (window.history.state?.startParams ?? {}) as Record<string, unknown>
  workspaceId = this.#store.selectSignal(selectRouteParam('workspaceId'))
  projectId = this.#store.selectSignal(selectRouteParam('appId'))
  buildId = this.#store.selectSignal(selectRouteParam('appVersionId'))
  uiFlowName = this.#store.selectSignal(selectQueryParam('uiFlowName'))
  themeId = this.#store.selectSignal(selectQueryParam('themeId'))
  language = this.#store.selectSignal(selectQueryParam('language'))
  locale = this.#store.selectSignal(selectQueryParam('locale'))

  showUiFlowInfo = signal<boolean>(false)

  reusableInfo = computed(() => ({
    appName: this.#applicationDetailsStore.appName(),
    appVersionId: this.#applicationDetailsStore.appVersionId(),
    branchName: this.#applicationDetailsStore.branchName(),
    status: this.#applicationDetailsStore.isVersionCommitted() ? 'COMMITTED' : 'WIP',
    type: this.#applicationDetailsStore.appType() === ApplicationType.APP ? 'Project' : 'Library',
  }))

  designerLink = computed(() => [
    '/',
    'guard',
    'workspace',
    this.workspaceId(),
    'projects',
    this.projectId(),
    'config',
    this.buildId(),
    'ui-flows',
    this.uiFlowResourceId(),
    'ui',
  ])
  uiFlowsLink = computed(() => [
    '/',
    'guard',
    'workspace',
    this.workspaceId(),
    'projects',
    this.projectId(),
    'config',
    this.buildId(),
    'ui-flows',
  ])

  uiFlowSessionUuid = toSignal(
    urlUpdate$.pipe(
      map((url: URL) => {
        const params: Record<string, string> = {}
        url.searchParams.forEach((value, key) => {
          params[key] = value
        })
        return params['uiFlowSessionUuid']
      }),
      filter((uiFlowSessionUuid) => !!uiFlowSessionUuid),
      takeUntilDestroyed(this.#destroyRef)
    )
  )
  uiFlowSession = signal<UiFlowSession>(null)
  uiFlowProcessInstances = signal<any[]>([])
  instanceDataOpened = computed(() => coerceBooleanProperty(this.#instanceDataOpenedQueryParam()))

  readonly FlxUiFlowRenderer = FlxUiFlowRendererPortal

  organizationId = getTenantId()

  // Base props are snapshotted once in ngOnInit: query params are dropped on
  // nav-away before this component is destroyed, so deriving them reactively
  // would re-render the React renderer with undefined props and trigger a
  // spurious re-init (e.g. theme fetch with uuid `undefined`). Those values
  // never change while the page is alive.
  // authToken is the one exception: it's sourced from OAuth events (not query
  // params), so it's safe to keep live — the renderer's init effects deliberately
  // exclude it from their deps, so a token-only change never re-inits the flow.
  reactProps = computed(() => ({ ...this.#baseReactProps, authToken: this.authToken() }))

  constructor() {
    effect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const processUuid = this.#processUuid() as string
      const uiFlowSessionUuid = this.uiFlowSessionUuid() as string
      const panelOpen = this.instanceDataOpened()

      if (!uiFlowSessionUuid || !panelOpen) {
        return
      }

      this.#uiFlowSessionsService
        .getUiFlowSession(uiFlowSessionUuid)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((response) => {
          this.uiFlowSession.set(response.session)
          this.uiFlowProcessInstances.set(response.processInstances)
        })
    })
  }

  ngOnInit(): void {
    this.#baseReactProps = {
      processApiPath: this.processApiPath,
      apiUrl: this.apiUrl,
      staticAssetsPath: this.staticAssetsPath,
      language: this.language(),
      themeId: this.themeId(),
      uiFlowName: this.uiFlowName(),
      appInfo: {
        appId: this.projectId(),
        appVersionId: this.buildId(),
        selectedProcessResourceId: this.uiFlowResourceId(),
      },
      workspaceId: this.workspaceId(),
      organizationId: this.organizationId,
      locale: this.locale(),
      cache: true,
      isDraft: true,
      startParams: this.startParams,
    }

    this.#authService.events
      .pipe(
        filter((event) => event.type === 'token_received'),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.authToken.set(this.#authService.getAccessToken())
      })

    this.#applicationDetailsStore.getApplicationVersion(this.projectId(), this.buildId())

    // SDK writes these to window.history directly; mirror into Router so 'merge' navs preserve them.
    urlUpdate$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((url) => {
      this.router.navigate([], {
        relativeTo: this.#route,
        queryParams: {
          uiFlowSessionUuid: url.searchParams.get('uiFlowSessionUuid'),
          modalId: url.searchParams.get('modalId'),
          viewId: url.searchParams.get('viewId'),
          chatSessionId: url.searchParams.get('chatSessionId'),
        },
        queryParamsHandling: 'merge',
        skipLocationChange: true,
      })
    })
  }

  ngOnDestroy(): void {
    this.#mutationObserver?.disconnect()
  }

  showInstanceData(): void {
    if (this.uiFlowSessionUuid()) {
      this.router.navigate([], {
        relativeTo: this.#route,
        queryParams: {
          instanceDataOpened: true,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    }
  }

  hideInstanceData(): void {
    this.router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        instanceDataOpened: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  onSizeChange(size: PanelSize): void {
    if (size === PanelSize.SMALL) {
      this.hideInstanceData()
    }
  }
}
